import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send } from "lucide-react";

export default function MobileMessages() {
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");

  useEffect(() => {
    api.get("/providers").then((r) => {
      setProviders(r.data);
      const doc = r.data.find((p) => p.role === "doctor") || r.data[0];
      if (doc) setRecipient(doc.id);
    });
  }, []);

  useEffect(() => {
    if (!recipient) return;
    const load = () => api.get(`/messages?with_user=${recipient}`).then((r) => setMessages(r.data));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [recipient]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !recipient) return;
    await api.post("/messages", { recipient_id: recipient, body: text });
    setText("");
    const r = await api.get(`/messages?with_user=${recipient}`);
    setMessages(r.data);
  };

  return (
    <div className="max-w-md mx-auto flex flex-col h-[calc(100vh-72px)]" data-testid="mobile-messages-page">
      <header className="px-5 pt-6 pb-3">
        <h1 className="font-display text-2xl font-bold text-stone-900">Messages</h1>
        <p className="text-stone-500 text-sm">Reach your care team directly.</p>
      </header>

      <div className="px-5 mb-2">
        <label className="text-xs font-medium uppercase tracking-wider text-stone-500 mb-1.5 block">Send to</label>
        <select data-testid="recipient-select" value={recipient || ""} onChange={(e) => setRecipient(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none">
          {providers.map((p) => {
            const label = `${p.full_name} (${p.role})${p.specialty ? ` · ${p.specialty}` : ""}`;
            return <option key={p.id} value={p.id}>{label}</option>;
          })}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
        {messages.length === 0 && <div className="text-center text-stone-500 text-sm py-8">No messages yet. Say hi!</div>}
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-brand-500 text-white rounded-br-md" : "bg-white border border-stone-200 text-stone-800 rounded-bl-md"}`}>
                <div>{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-brand-100" : "text-stone-400"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="border-t border-stone-200 bg-white px-3 py-3 flex gap-2 sticky bottom-16">
        <input data-testid="msg-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
          className="flex-1 px-4 py-3 rounded-full border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none text-sm" />
        <button data-testid="msg-send-btn" disabled={!text.trim()} className="w-12 h-12 rounded-full bg-brand-500 hover:bg-brand-700 text-white flex items-center justify-center disabled:opacity-50">
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
}
